import React, { useState, useEffect, useCallback } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import ImageUpload from "../../components/CustomComponents/ImageUpload/imageUpload";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import FormInput from "../../components/CustomComponents/Input/formInput";

const extractRelativePath = (path) => {
  if (!path) return "";
  if (!path.startsWith("http://") && !path.startsWith("https://")) {
    return String(path).replace(/\\/g, "/");
  }
  if (path.includes("uploads/")) {
    return `uploads/${path.split("uploads/").pop().replace(/\\/g, "/")}`;
  }
  if (path.includes("uploads\\")) {
    return `uploads/${path.split("uploads\\").pop().replace(/\\/g, "/")}`;
  }
  try {
    const url = new URL(path);
    const pathname = url.pathname.replace(/^\//, "");
    return pathname.replace(/\\/g, "/");
  } catch {
    return "";
  }
};

function PrintingReceipts({ embedInSettings = false }) {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData, settingsData } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [imageState, setImageState] = useState({
    imageUrl: "",
    imageFile: null,
    shouldDelete: false, // Add this flag
  });

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  

  const [formState, setFormState] = useState({
    CompID: CompID,
    CompanyName: "ServeIQ",
    CompanyLogo: null,
    isShowVATNumber: false,
    isShowVATRegNo: true,
    CustomFontSize: "16.00",
    FooterText: "",
    PrintLogoAndAddressOnKitchen: false,
    PrintBusinessAddress: false,
    PrintPriceOnKitchenReceipt: false,
    ShowFacebooklink: false,
    ShowInstagramlink: false,
    ShowContactNumber: false,
    ShowReturnPolicy: false,
    ShowWebsite: false,
    ShowEmailAddress: false,
    isShowFBRInfo: false,
    RestaurantAddress: "",
    s_ID: 0,
    s_VAT: "7.95",
    s_VATRegNo: "",
    s_VARType: "inclusive",
    s_ServiceCharges: "6.84",
    deliveryCharges: "0.10",
    s_ServicesChargesOnCovers: "",
    s_MinOrderAmount: "",
    s_DeliveryTime: "",
    s_Facebooklink: "",
    s_InstagramLink: "",
    s_WebSiteLink: "",
    s_ContactNumber: "0175 338 6565",
    s_ReturnPolicy: "",
    isShowWarningOnDeletingCustomerData: false,
    s_IsUpdatefromKitchen: false,
    manageCoversOnTablePlan: false,
    s_TableReservationMarginTime: 0,
    s_TableReservedRelesaeTime: 0,
    s_ShowingReserveredTableTime: 0,
    s_ShowCancelledOrderDuration: 0,
    s_VisibleCancelOrderDaysinCancelTab: 0,
    s_IsHaveLoyalty: false,
    s_PointperPurchase: 0,
    s_PointSperDollar: 0,
    tV_ISOrderStatus: false,
    tV_ISOrderType: false,
    tV_ISOrderNumber: false,
    tV_ISOrderOwner: false,
    isActivePreOrderModule: false,
    preOrderDayRange: "",
    CompEmail: "",
    CompFacebooklink: "",
    CompInstagramLink: "",
    CompWebsite: "",
    CompCurrency: "GBP",
  });

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);

      // Use Redux state for company and profile data, but still fetch receipt data
      if (!settingsData || !profileData) {
        showErrorToast("Company data not available. Please refresh the page.");
        setLoading(false);
        return;
      }

      const receiptResponse = await axiosInstance.get(
        `${APIs.GET_RECEIPT_INFO}`
      );
      const receiptData = receiptResponse.data.data;
      const companyData = settingsData;
      const profileDataFromState = profileData;

      setFormState((prev) => ({
        ...prev,
        CompanyName: receiptData?.companyName || "ServeIQ",
        CompanyLogo: receiptData?.companyLogo || "",
        isShowVATNumber: !!receiptData?.isShowVATNumber,
        isShowVATRegNo: !!receiptData?.isShowVATRegNo || false,
        CustomFontSize: receiptData?.customFontSize || "16.00",
        FooterText: receiptData?.footerText || "",
        PrintLogoAndAddressOnKitchen:
          !!receiptData?.printLogoAndAddressOnKitchen,
        PrintBusinessAddress: !!receiptData?.printBusinessAddress,
        PrintPriceOnKitchenReceipt: !!receiptData?.printPriceOnKitchenReceipt,
        ShowFacebooklink: !!receiptData?.showFacebooklink,
        ShowInstagramlink: !!receiptData?.showInstagramlink,
        ShowContactNumber: !!receiptData?.showContactNumber,
        ShowReturnPolicy: !!receiptData?.showReturnPolicy,
        ShowWebsite: !!receiptData?.showWebsite,
        ShowEmailAddress: !!receiptData?.showEmailAddress,
        isShowFBRInfo: !!receiptData?.isShowFBRInfo || false,
        RestaurantAddress: receiptData?.restaurantAddress || "",
        s_ID: companyData.s_ID || 0,
        CompID: companyData.CompID || CompID,
        s_VAT: companyData.s_VAT || "7.95",
        s_VATRegNo: companyData.s_VATRegNo || "",
        s_VARType: companyData.s_VARType || "inclusive",
        s_ServiceCharges: companyData.s_ServiceCharges || "6.84",
        deliveryCharges: companyData.deliveryCharges || "0.10",
        s_ServicesChargesOnCovers: companyData.s_ServicesChargesOnCovers || "",
        s_MinOrderAmount: companyData.s_MinOrderAmount || "",
        s_DeliveryTime: companyData.s_DeliveryTime || "",
        s_Facebooklink: companyData.s_Facebooklink || "",
        s_InstagramLink: companyData.s_InstagramLink || "",
        s_WebSiteLink: companyData.s_WebSiteLink || "",
        s_ContactNumber: companyData.s_ContactNumber || "0175 338 6565",
        s_ReturnPolicy: companyData.s_ReturnPolicy || "",
        isShowWarningOnDeletingCustomerData:
          !!companyData.isShowWarningOnDeletingCustomerData,
        s_IsUpdatefromKitchen: !!companyData.s_IsUpdatefromKitchen,
        manageCoversOnTablePlan: !!companyData.manageCoversOnTablePlan,
        s_TableReservationMarginTime:
          companyData.s_TableReservationMarginTime || 0,
        s_TableReservedRelesaeTime: companyData.s_TableReservedRelesaeTime || 0,
        s_ShowingReserveredTableTime:
          companyData.s_ShowingReserveredTableTime || 0,
        s_ShowCancelledOrderDuration:
          companyData.s_ShowCancelledOrderDuration || 0,
        s_VisibleCancelOrderDaysinCancelTab:
          companyData.s_VisibleCancelOrderDaysinCancelTab || 0,
        s_IsHaveLoyalty: !!companyData.s_IsHaveLoyalty,
        s_PointperPurchase: companyData.s_PointperPurchase || 0,
        s_PointSperDollar: companyData.s_PointSperDollar || 0,
        tV_ISOrderStatus: !!companyData.tV_ISOrderStatus,
        tV_ISOrderType: !!companyData.tV_ISOrderType,
        tV_ISOrderNumber: !!companyData.tV_ISOrderNumber,
        tV_ISOrderOwner: !!companyData.tV_ISOrderOwner,
        isActivePreOrderModule: !!companyData.isActivePreOrderModule,
        preOrderDayRange: companyData.preOrderDayRange || "",
        CompEmail: profileDataFromState?.compEmail || "",
        CompFacebooklink: profileDataFromState?.compFacebooklink || "",
        CompInstagramLink: profileDataFromState?.compInstagramLink || "",
        CompWebsite: profileDataFromState?.compWebsite || "",
        CompCurrency: profileDataFromState?.compCurrency || "GBP",
      }));

      setImageState((prev) => ({
        ...prev,
        imageUrl: receiptData?.companyLogo || "",
        shouldDelete: false, // Reset the flag
      }));
    } catch (err) {
      showErrorToast("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [CompID, settingsData, profileData]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleInputChange = useCallback((e, isDropdown = false) => {
    const value = isDropdown
      ? e.value
      : e.target.type === "checkbox"
      ? e.target.checked
      : e.target.value;
    const name = isDropdown ? e.name : e.target.name;
    setFormState((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      let sanitizedLogoImage = extractRelativePath(formState?.CompanyLogo);

      if (imageState.shouldDelete) {
        sanitizedLogoImage = "";
      } else if (imageState.imageFile) {
        const imageFormData = new FormData();
        imageFormData.append("ImageFile", imageState.imageFile);

        const imageResponse = await axiosInstance.post(
          `${APIs.POST_UPLOAD_PRODUCT_IMAGE}`,
          imageFormData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );

        if (!imageResponse.data?.status) {
          throw new Error(
            imageResponse.data?.message ||
              imageResponse.data?.Message ||
              "Image upload failed"
          );
        }

        sanitizedLogoImage = extractRelativePath(
          imageResponse.data.data?.filePath || sanitizedLogoImage
        );
      } else if (
        imageState.imageUrl &&
        !imageState.imageUrl.startsWith("blob:")
      ) {
        // Media library selection (ImageUpload never passes imageFile)
        sanitizedLogoImage = extractRelativePath(imageState.imageUrl);
      }

      const patchData = {
        CompID: formState.CompID,
        ...formState,
        CompanyLogo: sanitizedLogoImage,
      };

      const response = await axiosInstance.post(
        APIs.POST_UPDATE_RECEIPT_INFO,
        patchData
      );

      if (!response.data?.status) {
        throw new Error(
          response.data?.message ||
            response.data?.Message ||
            "Failed to update settings"
        );
      }

      if (imageState.imageUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(imageState.imageUrl);
      }

      setFormState((prev) => ({ ...prev, CompanyLogo: sanitizedLogoImage }));
      setImageState({
        imageUrl: sanitizedLogoImage,
        imageFile: null,
        shouldDelete: false,
      });

      showSuccessToast(
        response.data.message || "Settings updated successfully"
      );
    } catch (error) {
      showErrorToast(error.message || "Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };
  const handleRemoveImage = () => {
    setImageState({
      imageUrl: "",
      imageFile: null,
      shouldDelete: true, // Mark for deletion
    });
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const mainContent = (
      <div className="row g-0 justify-content-center mx-0">
        <div className="col-12">
          <div className="mb-0 px-0">
            <div className="card border-0 shadow-sm rounded-0">
              <div className="card-body p-4">
                <form onSubmit={handleSubmit}>
                  <div className="row g-4 align-items-center border rounded-3 p-4 mt-4 mb-4 bg-white">
               
                    <div className="col-md-4">
                      <div className="text-center p-3 border rounded-3 h-100 d-flex flex-column justify-content-center">
                        <h4 className="mb-3 fw-semibold text-muted">Logo Image</h4>
                        <div className="d-flex flex-column align-items-center">
                          <ImageUpload
                            key={
                              imageState.imageFile
                                ? "file-present"
                                : imageState.imageUrl
                            }
                            imageUrl={imageState.imageUrl}
                            imageFile={imageState.imageFile}
                            setImage={(url, file) =>
                              setImageState({
                                imageUrl: url,
                                imageFile: file,
                                shouldDelete: false,
                              })
                            }
                            mediaLibrarySubtitle="Pick a logo from your library or upload a new image."
                            onRemove={handleRemoveImage}
                            height="120px"
                            width="150px"
                            useGrid={false}
                            centerButton={true}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-8">
                      <div className="row row-cols-1 row-cols-sm-2 g-3">
                        <div className="col">
                          <ToggleSwitch
                            isChecked={formState.PrintLogoAndAddressOnKitchen}
                            onToggle={() =>
                              setFormState((prev) => ({
                                ...prev,
                                PrintLogoAndAddressOnKitchen:
                                  !prev.PrintLogoAndAddressOnKitchen,
                              }))
                            }
                            label="Print Logo"
                            name="PrintLogoAndAddressOnKitchen"
                            themeColor={themeColor}
                          />
                        </div>
                        <div className="col">
                          <ToggleSwitch
                            isChecked={formState.PrintPriceOnKitchenReceipt}
                            onToggle={() =>
                              setFormState((prev) => ({
                                ...prev,
                                PrintPriceOnKitchenReceipt:
                                  !prev.PrintPriceOnKitchenReceipt,
                              }))
                            }
                            label="Print Price on Kitchen Receipt"
                            name="PrintPriceOnKitchenReceipt"
                            themeColor={themeColor}
                          />
                        </div>
                        <div className="col">
                          <ToggleSwitch
                            isChecked={formState.isShowVATNumber}
                            onToggle={() =>
                              setFormState((prev) => ({
                                ...prev,
                                isShowVATNumber: !prev.isShowVATNumber,
                              }))
                            }
                            label="Show VAT percentage"
                            name="isShowVATNumber"
                            themeColor={themeColor}
                          />
                        </div>
                        <div className="col">
                          <ToggleSwitch
                            isChecked={formState.isShowVATRegNo}
                            onToggle={() =>
                              setFormState((prev) => ({
                                ...prev,
                                isShowVATRegNo: !prev.isShowVATRegNo,
                              }))
                            }
                            label="Show VAT Registration Number"
                            name="isShowVATRegNo"
                            themeColor={themeColor}
                          />
                        </div>
                        <div className="col">
                          <ToggleSwitch
                            isChecked={formState.ShowContactNumber}
                            onToggle={() =>
                              setFormState((prev) => ({
                                ...prev,
                                ShowContactNumber: !prev.ShowContactNumber,
                              }))
                            }
                            label="Show Contact Number on Receipt"
                            name="ShowContactNumber"
                            themeColor={themeColor}
                          />
                        </div>
                        <div className="col">
                          <ToggleSwitch
                            isChecked={formState.ShowReturnPolicy}
                            onToggle={() =>
                              setFormState((prev) => ({
                                ...prev,
                                ShowReturnPolicy: !prev.ShowReturnPolicy,
                              }))
                            }
                            label="Show Return Policy on Receipt"
                            name="ShowReturnPolicy"
                            themeColor={themeColor}
                          />
                        </div>
                        <div className="col">
                          <ToggleSwitch
                            isChecked={formState.ShowWebsite}
                            onToggle={() =>
                              setFormState((prev) => ({
                                ...prev,
                                ShowWebsite: !prev.ShowWebsite,
                              }))
                            }
                            label="Show Website URL on Receipt"
                            name="ShowWebsite"
                            themeColor={themeColor}
                          />
                        </div>
                        <div className="col">
                          <ToggleSwitch
                            isChecked={formState.ShowEmailAddress}
                            onToggle={() =>
                              setFormState((prev) => ({
                                ...prev,
                                ShowEmailAddress: !prev.ShowEmailAddress,
                              }))
                            }
                            label="Show Email Address on Receipt"
                            name="ShowEmailAddress"
                            themeColor={themeColor}
                          />
                        </div>
                        <div className="col">
                          <ToggleSwitch
                            isChecked={formState.ShowFacebooklink}
                            onToggle={() =>
                              setFormState((prev) => ({
                                ...prev,
                                ShowFacebooklink: !prev.ShowFacebooklink,
                              }))
                            }
                            label="Show Facebook Link on Receipt"
                            name="ShowFacebooklink"
                            themeColor={themeColor}
                          />
                        </div>
                        <div className="col">
                          <ToggleSwitch
                            isChecked={formState.ShowInstagramlink}
                            onToggle={() =>
                              setFormState((prev) => ({
                                ...prev,
                                ShowInstagramlink: !prev.ShowInstagramlink,
                              }))
                            }
                            label="Show Instagram Link on Receipt"
                            name="ShowInstagramlink"
                            themeColor={themeColor}
                          />
                        </div>
                        
                      </div>
                    </div>
                    
                  </div>

                  <div className="row g-4">
                    <div className="col-md-6">
                      <label className="mb-1">Company Name</label>
                      <FormInput
                        type="text"
                        value={formState.CompanyName}
                        onchange={handleInputChange}
                        placeHolder="Enter Company Name"
                        name="CompanyName"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="mb-1">Restaurant Address</label>
                      <FormInput
                        type="text"
                        value={formState.RestaurantAddress}
                        onchange={handleInputChange}
                        placeHolder="Enter Restaurant Address"
                        name="RestaurantAddress"
                      />
                    </div>
                    <div className="col-md-6 d-none">
                      <label className="mb-1">Custom Font Size</label>
                      <FormInput
                        type="number"
                        value={formState.CustomFontSize}
                        onchange={handleInputChange}
                        placeHolder="Enter Custom Font Size"
                        name="CustomFontSize"
                      />
                    </div>
                    <div className="col-12">
                      <label className="mb-1">Footer Text</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={formState.FooterText}
                        onChange={handleInputChange}
                        placeholder="Enter Custom Text"
                        name="FooterText"
                      />
                    </div>
                  </div>

                  <div className="mt-4 d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => fetchSettings()}
                      disabled={loading}
                    >
                      Reset
                    </button>
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={loading}
                      style={{
                        backgroundColor: themeColor,
                        background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                        border: `1px solid ${themeColor}`,
                        boxShadow: `0 4px 15px ${themeColor}40`,
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = "translateY(-2px)";
                        e.target.style.boxShadow = `0 6px 20px ${themeColor}60`;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = `0 4px 15px ${themeColor}40`;
                      }}
                    >
                      Save All Settings
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="col-5 d-none ">
          <div className="mt-3">
            <h4 className="d-flex align-items-center mb-2 d-none">Receipt Preview</h4>
            <p>Preview of the receipt based on current settings.</p>
            <div className="card">
              <div className="card-body d-flex justify-content-center">
                <div
                  className="receipt-preview"
                  style={{
                    fontSize: formState.CustomFontSize
                      ? `${formState.CustomFontSize}px`
                      : "14px",
                    fontFamily: '"Courier New", Courier, monospace',
                    width: "280px",
                    background: "#ffffff",
                    border: "2px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "20px 15px",
                    lineHeight: "1.4",
                    color: "#333",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "-1px",
                      left: "0",
                      right: "0",
                      height: "8px",
                      background:
                        "repeating-linear-gradient(90deg, #e0e0e0 0px, #e0e0e0 8px, transparent 8px, transparent 16px)",
                      borderRadius: "4px 4px 0 0",
                    }}
                  ></div>

                  {/* Header Section */}
                  <div style={{ textAlign: "center", marginBottom: "16px" }}>
                    {formState.PrintLogoAndAddressOnKitchen &&
                      imageState.imageUrl && (
                        <div style={{ marginBottom: "8px" }}>
                          <img
                            src={imageState.imageUrl}
                            alt="Logo"
                            style={{
                              maxHeight: "40px",
                              maxWidth: "100px",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      )}

                    {formState.PrintLogoAndAddressOnKitchen && (
                      <div
                        style={{
                          fontSize: `${Math.max(
                            parseInt(formState.CustomFontSize) + 2,
                            16
                          )}px`,
                          fontWeight: "bold",
                          marginBottom: "6px",
                          letterSpacing: "0.5px",
                        }}
                      ></div>
                    )}
                    <div
                      style={{
                        fontSize: `${Math.max(
                          parseInt(formState.CustomFontSize) + 2,
                          16
                        )}px`,
                        fontWeight: "bold",
                        marginBottom: "6px",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {formState.CompanyName.toUpperCase()}
                    </div>

                    {formState.PrintBusinessAddress && formState.RestaurantAddress && (
                      <div
                        style={{
                          fontSize: `${Math.max(
                            parseInt(formState.CustomFontSize) - 2,
                            10
                          )}px`,
                          lineHeight: "1.3",
                          color: "#555",
                          marginBottom: "8px",
                        }}
                      >
                        {formState.RestaurantAddress}
                        {formState.ShowContactNumber && (
                          <>
                            <br />
                            Tel: {formState.s_ContactNumber}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      borderTop: "1px dashed #999",
                      margin: "12px 0",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        left: "-15px",
                        right: "-15px",
                        height: "1px",
                        background:
                          "repeating-linear-gradient(90deg, transparent 0px, transparent 3px, #999 3px, #999 6px)",
                      }}
                    ></div>
                  </div>

                  <div style={{ textAlign: "center", marginBottom: "16px" }}>
                    <div
                      style={{
                        fontSize: `${Math.max(
                          parseInt(formState.CustomFontSize) + 3,
                          18
                        )}px`,
                        fontWeight: "bold",
                        marginBottom: "6px",
                        letterSpacing: "1px",
                      }}
                    >
                      ORDER #62
                    </div>
                    <div
                      style={{
                        fontSize: `${Math.max(
                          parseInt(formState.CustomFontSize) - 1,
                          11
                        )}px`,
                        color: "#666",
                        lineHeight: "1.3",
                      }}
                    >
                      <div style={{ marginBottom: "2px" }}>
                        Collection Order
                      </div>
                      <div style={{ marginBottom: "2px" }}>
                        29 Aug 2025 at 12:09 PM
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          margin: "2px",
                        }}
                      >
                        <span>Cashier:</span>
                        <span>ServIQ</span>
                      </div>
                      <div>Invoice: PID2518298423895</div>
                    </div>
                  </div>

                  <div
                    style={{ borderTop: "1px dashed #999", margin: "12px 0" }}
                  ></div>

                  <div style={{ marginBottom: "16px" }}>
                    <div
                      style={{
                        fontSize: `${Math.max(
                          parseInt(formState.CustomFontSize) - 1,
                          11
                        )}px`,
                        lineHeight: "1.5",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "4px",
                        }}
                      >
                        <span>1x Prawns & 7up</span>
                        <span style={{ fontWeight: "bold" }}>
                          {getCurrencySymbol(formState.CompCurrency)}12.00
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "4px",
                        }}
                      >
                        <span>1x Diet Coke 330ml</span>
                        <span style={{ fontWeight: "bold" }}>
                          {getCurrencySymbol(formState.CompCurrency)}3.00
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "4px",
                        }}
                      >
                        <span>1x Cold Drinks 7up</span>
                        <span style={{ fontWeight: "bold" }}>
                          {getCurrencySymbol(formState.CompCurrency)}26.40
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                    style={{ borderTop: "1px dashed #999", margin: "12px 0" }}
                  ></div>

                  <div
                    style={{
                      fontSize: `${Math.max(
                        parseInt(formState.CustomFontSize) - 1,
                        11
                      )}px`,
                      marginBottom: "16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "3px",
                      }}
                    >
                      <span>Subtotal:</span>
                      <span>
                        {getCurrencySymbol(formState.CompCurrency)}48.40
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "3px",
                      }}
                    >
                      <span>Extra Charges:</span>
                      <span>
                        {getCurrencySymbol(formState.CompCurrency)}5.00
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "3px",
                      }}
                    >
                      <span>
                        Service Charge ({formState.s_ServiceCharges}%):
                      </span>
                      <span>
                        {getCurrencySymbol(formState.CompCurrency)}
                        {formState.s_ServiceCharges}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "3px",
                      }}
                    >
                      <span>Delivery Charges:</span>
                      <span>
                        {getCurrencySymbol(formState.CompCurrency)}
                        {formState.deliveryCharges}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "3px",
                      }}
                    >
                      <span>Tip Amount:</span>
                      <span>
                        {getCurrencySymbol(formState.CompCurrency)}2.24
                      </span>
                    </div>
                    {formState.isShowVATRegNo && formState.s_VATRegNo && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "3px",
                        }}
                      >
                        <span>VAT Reg No:</span>
                        <span>{formState.s_VATRegNo}</span>
                      </div>
                    )}
                    {formState.isShowVATNumber && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "3px",
                        }}
                      >
                        <span>
                          VAT ({formState.s_VAT}% {formState.s_VARType}):
                        </span>
                        <span>
                          {getCurrencySymbol(formState.CompCurrency)}7.95
                        </span>
                      </div>
                    )}
                    {formState.isShowFBRInfo && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "3px",
                        }}
                      >
                        <span>FBR Info:</span>
                        <span>FBR Placeholder</span>
                      </div>
                    )}
                  </div>

                  <div
                    style={{ borderTop: "2px solid #333", margin: "12px 0" }}
                  ></div>

                  <div
                    style={{
                      textAlign: "center",
                      marginBottom: "16px",
                      padding: "8px 0",
                    }}
                  >
                    <div
                      style={{
                        fontSize: `${Math.max(
                          parseInt(formState.CustomFontSize) + 1,
                          16
                        )}px`,
                        fontWeight: "bold",
                        marginBottom: "4px",
                      }}
                    >
                      TOTAL
                    </div>
                    <div
                      style={{
                        fontSize: `${Math.max(
                          parseInt(formState.CustomFontSize) + 6,
                          22
                        )}px`,
                        fontWeight: "bold",
                        letterSpacing: "1px",
                      }}
                    >
                      {getCurrencySymbol(formState.CompCurrency)}82.58
                    </div>
                  </div>

                  <div
                    style={{ borderTop: "1px dashed #999", margin: "12px 0" }}
                  ></div>

                  {formState.FooterText && (
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: `${Math.max(
                          parseInt(formState.CustomFontSize) - 1,
                          11
                        )}px`,
                        fontStyle: "italic",
                        color: "#555",
                        marginBottom: "12px",
                        lineHeight: "1.4",
                      }}
                    >
                      {formState.FooterText}
                    </div>
                  )}

                  {formState.ShowReturnPolicy && (
                    <div
                      style={{
                        fontSize: `${Math.max(
                          parseInt(formState.CustomFontSize) - 2,
                          10
                        )}px`,
                        color: "#666",
                        textAlign: "center",
                        marginBottom: "12px",
                        lineHeight: "1.3",
                      }}
                    >
                      Return Policy: Items can be returned within 30 days with
                      receipt.
                    </div>
                  )}

                  <div
                    style={{
                      textAlign: "center",
                      fontSize: `${Math.max(
                        parseInt(formState.CustomFontSize),
                        14
                      )}px`,
                      fontWeight: "bold",
                      letterSpacing: "2px",
                      marginTop: "12px",
                    }}
                  >
                    THANK YOU!
                  </div>
                  <div
                    style={{
                      fontSize: `${Math.max(
                        parseInt(formState.CustomFontSize) - 2,
                        10
                      )}px`,
                      color: "#666",
                    }}
                  >
                    {formState.ShowWebsite && formState.CompWebsite && (
                      <div>Website: {formState.CompWebsite}</div>
                    )}
                    {formState.ShowEmailAddress && formState.CompEmail && (
                      <div>Email: {formState.CompEmail}</div>
                    )}
                    {formState.ShowFacebooklink &&
                      formState.CompFacebooklink && (
                        <div>Facebook: {formState.CompFacebooklink}</div>
                      )}
                    {formState.ShowInstagramlink &&
                      formState.CompInstagramLink && (
                        <div>Instagram: {formState.CompInstagramLink}</div>
                      )}
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      bottom: "-1px",
                      left: "0",
                      right: "0",
                      height: "8px",
                      background:
                        "repeating-linear-gradient(90deg, #e0e0e0 0px, #e0e0e0 8px, transparent 8px, transparent 16px)",
                      borderRadius: "0 0 4px 4px",
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );

  if (embedInSettings) {
    return (
      <GridSectionLoader loading={loading} minHeight="280px">
        {mainContent}
      </GridSectionLoader>
    );
  }
  return (
    <div className="container-fluid py-4 px-0">
      <div className="d-flex align-items-center mb-3 px-0">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/">Home</a>
            </li>
            <li className="breadcrumb-item active">Printing Receipts</li>
          </ol>
          <h1 className="page-header mb-0">Printing Receipts Configuration</h1>
        </div>
      </div>
      <GridSectionLoader loading={loading} minHeight="320px">
        {mainContent}
      </GridSectionLoader>
    </div>
  );
}

export default PrintingReceipts;